import type { CSSProperties, FC, ReactNode } from 'react'
import type { CSSObject, PaletteMode, SxProps, Theme } from '@mui/material/styles'
import { styled } from '@mui/material/styles'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import type { BadgeTone } from '@/theme/badge-tokens'
import {
  badgeActiveBg,
  badgeHoverBg,
  getBadgeTokens,
} from '@/theme/badge-tokens'
import { BADGE_SIZE_STYLES } from './badgeSizes'

export type BadgeSize = 'sm' | 'md' | 'lg'
export type BadgeIconPosition = 'auto' | 'before' | 'after'

const BADGE_BASE_STYLES: CSSObject = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4px',
  borderRadius: '99px',
  fontWeight: 500,
  fontFamily:
    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
  whiteSpace: 'nowrap',
  lineHeight: 1,
  border: 'none',
  boxShadow: 'none',
  boxSizing: 'border-box',
}

const toneStyles = (tone: BadgeTone, mode: PaletteMode): CSSObject => {
  const tokens = getBadgeTokens(mode)

  return {
    backgroundColor: tokens[tone].bg,
    color: tokens[tone].fg,
  }
}

const sizeStyles: Record<BadgeSize, CSSObject> = {
  sm: {
    fontSize: BADGE_SIZE_STYLES.sm.fontSize,
    padding: BADGE_SIZE_STYLES.sm.padding,
    minHeight: BADGE_SIZE_STYLES.sm.minHeight,
  },
  md: {
    fontSize: BADGE_SIZE_STYLES.md.fontSize,
    padding: BADGE_SIZE_STYLES.md.padding,
    minHeight: BADGE_SIZE_STYLES.md.minHeight,
  },
  lg: {
    fontSize: BADGE_SIZE_STYLES.lg.fontSize,
    padding: BADGE_SIZE_STYLES.lg.padding,
    minHeight: BADGE_SIZE_STYLES.lg.minHeight,
  },
}

const interactiveStyles = (tone: BadgeTone, mode: PaletteMode): CSSObject => {
  const tokens = getBadgeTokens(mode)

  return {
    appearance: 'none',
    cursor: 'pointer',
    margin: 0,
    textAlign: 'inherit',
    transition: 'background-color 150ms ease',
    '&:hover': {
      backgroundColor: badgeHoverBg(tone, mode),
      color: tokens[tone].fg,
    },
    '&:active': {
      backgroundColor: badgeActiveBg(tone, mode),
      color: tokens[tone].fg,
    },
    '&:focus-visible': {
      outline: `2px solid color-mix(in srgb, ${tokens[tone].fg} 35%, transparent)`,
      outlineOffset: 1,
    },
  }
}

const badgeStyles = (
  tone: BadgeTone,
  size: BadgeSize,
  mode: PaletteMode
): CSSObject => ({
  ...BADGE_BASE_STYLES,
  ...toneStyles(tone, mode),
  ...sizeStyles[size],
})

const BadgeRoot = styled('span', {
  shouldForwardProp: (prop) => prop !== 'tone' && prop !== 'size',
})<{ tone: BadgeTone; size: BadgeSize }>(({ theme, tone, size }) =>
  badgeStyles(tone, size, theme.palette.mode)
)

const BadgeButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'tone' && prop !== 'size',
})<{ tone: BadgeTone; size: BadgeSize }>(({ theme, tone, size }) => ({
  ...badgeStyles(tone, size, theme.palette.mode),
  ...interactiveStyles(tone, theme.palette.mode),
}))

export interface BadgeProps {
  children?: ReactNode
  tone?: BadgeTone
  size?: BadgeSize
  icon?: ReactNode | null
  iconPosition?: BadgeIconPosition
  editable?: boolean
  onClick?: () => void
  style?: CSSProperties
  sx?: SxProps<Theme>
  className?: string
  'data-testid'?: string
}

const isNewBadgeLabel = (children: ReactNode): boolean => {
  if (typeof children === 'string') {
    return /^new$/i.test(children.trim())
  }
  return false
}

const resolveIconPlacement = (
  iconPosition: BadgeIconPosition,
  children: ReactNode
): 'before' | 'after' => {
  if (iconPosition === 'before') return 'before'
  if (iconPosition === 'after') return 'after'
  return isNewBadgeLabel(children) ? 'before' : 'after'
}

const editableChevronSx = {
  fontSize: 12,
  width: 12,
  height: 12,
  opacity: 0.75,
  flexShrink: 0,
}

export const Badge: FC<BadgeProps> = ({
  children,
  tone = 'neutral',
  size = 'md',
  icon = null,
  iconPosition = 'auto',
  editable = false,
  onClick,
  style,
  sx,
  className,
  'data-testid': dataTestId = 'badge',
}) => {
  const iconPlacement = resolveIconPlacement(iconPosition, children)
  const isInteractive = Boolean(onClick)
  const Root = isInteractive ? BadgeButton : BadgeRoot

  return (
    <Root
      tone={tone}
      size={size}
      type={isInteractive ? 'button' : undefined}
      onClick={onClick}
      style={style}
      className={className}
      data-testid={dataTestId}
      aria-haspopup={editable && isInteractive ? 'listbox' : undefined}
      sx={sx}
    >
      {icon && iconPlacement === 'before' ? icon : null}
      {children}
      {icon && iconPlacement === 'after' ? icon : null}
      {editable && isInteractive ? (
        <KeyboardArrowDownIcon sx={editableChevronSx} />
      ) : null}
    </Root>
  )
}
